import { RefObject, useCallback } from 'react';
import { zoomLevelStore } from '../store/zoomLevel';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';

export const useDragElement = ({
  setDraggingElement,
  setDraggingElementOffset,
  onDragEnd = () => Promise.resolve(),
  validDropZoneRef,
  droppableContainerRef,
  preventResetOffsetOnDragEnd = false,
  fps
}: {
  setDraggingElement: (dragId: string | null) => void;
  setDraggingElementOffset: (offset: number) => void;
  onDragEnd?: (offset: number, dragId: string) => void;
  validDropZoneRef: RefObject<HTMLDivElement>;
  droppableContainerRef: RefObject<HTMLDivElement>;
  preventResetOffsetOnDragEnd?: boolean;
  fps: number;
}) => {
  const onMouseDown = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | MouseEvent,
      dragElementId: string,
      maxOffset = Infinity,
      minOffset = -Infinity
    ) => {
      if ('button' in e && e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      document.body.style.cursor = 'ew-resize';
      EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: true });

      let autoScrollInterval: NodeJS.Timeout | null = null;
      const droppableContainer = droppableContainerRef.current;
      if (!droppableContainer) {
        return;
      }
      const droppableContainerRect = droppableContainer.getBoundingClientRect();
      const initialPosition = e.clientX - droppableContainerRect.left;

      setDraggingElement(dragElementId);
      setDraggingElementOffset(0);
      let lastValidOffset = 0;

      const isValidDrag = (timeOffset: number) => {
        const isValid = timeOffset >= minOffset && timeOffset <= maxOffset;
        return isValid;
      };

      const isDropInsideParent = (e: MouseEvent) => {
        if (!validDropZoneRef) {
          return true;
        }
        const rect = validDropZoneRef.current?.getBoundingClientRect();
        if (rect) {
          const isDropInside = e.clientY >= rect.top && e.clientY <= rect.bottom;
          return isDropInside;
        }
        return false;
      };

      const autoScroll = (e: MouseEvent) => {
        if (autoScrollInterval) {
          clearInterval(autoScrollInterval);
          autoScrollInterval = null;
        }
        if (!droppableContainer.parentElement) {
          return;
        }
        const shouldScrollLeft = e.clientX < 80;
        const scrollLeftPossible = droppableContainer.parentElement.scrollLeft > 0;

        if (shouldScrollLeft && !scrollLeftPossible) {
          return;
        }

        const shouldScrollRight = e.clientX > window.innerWidth - 10;
        const scrollRightPossible =
          droppableContainer.parentElement.scrollLeft <
          droppableContainer.parentElement.scrollWidth - droppableContainer.parentElement.clientWidth;

        if (shouldScrollRight && !scrollRightPossible) {
          return;
        }

        if (shouldScrollLeft) {
          droppableContainer.parentElement.scrollBy({
            left: -50,
            behavior: 'smooth'
          });
        } else if (shouldScrollRight) {
          droppableContainer.parentElement.scrollBy({
            left: +50,
            behavior: 'smooth'
          });
        } else {
          return;
        }

        autoScrollInterval = setInterval(() => {
          updateDragValues(e);
        }, 50);
      };

      const updateDragValues = (e: MouseEvent) => {
        const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
        const droppableContainer = droppableContainerRef.current;
        if (!droppableContainer) {
          return;
        }
        const droppableContainerRect = droppableContainer.getBoundingClientRect();

        let timeOffset = (e.clientX - droppableContainerRect.left - initialPosition) / zoomLevel / fps;

        if (!isValidDrag(timeOffset)) {
          if (timeOffset > maxOffset) {
            timeOffset = maxOffset;
          } else if (timeOffset < minOffset) {
            timeOffset = minOffset;
          }
        }
        lastValidOffset = timeOffset;
        setDraggingElementOffset(timeOffset);
        autoScroll(e);
      };

      const onMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        updateDragValues(e);
      };

      const onMouseUp = async (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        document.body.style.cursor = 'default';
        const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
        EventBus.dispatch(CustomEvents.NewTimelineEntityDraggingStateChanged, { isDraggingTimelineEntity: false });
        const droppableContainer = droppableContainerRef.current;
        if (!droppableContainer) {
          return;
        }
        const droppableContainerRect = droppableContainer.getBoundingClientRect();

        const timeOffset = (e.clientX - droppableContainerRect.left - initialPosition) / zoomLevel / fps;

        setDraggingElement(null);
        if (isDropInsideParent(e)) {
          if (isValidDrag(timeOffset)) {
            await onDragEnd(timeOffset, dragElementId);
          } else {
            await onDragEnd(lastValidOffset, dragElementId);
          }
        } else {
          setDraggingElementOffset(0);
        }
        if (!preventResetOffsetOnDragEnd) {
          setDraggingElementOffset(0);
        }
        onCleanUp();
      };

      const onCleanUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = 'default';
        if (autoScrollInterval) {
          clearInterval(autoScrollInterval);
          autoScrollInterval = null;
        }
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return onCleanUp;
    },
    [
      droppableContainerRef,
      setDraggingElement,
      setDraggingElementOffset,
      validDropZoneRef,
      preventResetOffsetOnDragEnd,
      onDragEnd,
      fps
    ]
  );

  return { onMouseDown };
};
